// The Farming unit editor's list: single-block fields as plain block rows,
// multi-block (and empty) fields as collapsible groups with an editable name.
// Rows are selectable (Shift/Cmd/Ctrl-click to multi-select) and draggable into
// or out of groups; dragging a selected row drags the whole selection.
const React = require('react');
const { useState } = require('react');
const { useTranslation } = require('react-i18next');
const { isGroupField } = require('./layoutHelpers');

const DRAG_TYPE = 'text/plain';

// The dragged block keys travel as a JSON array.
const readDraggedKeys = (event) => {
  try {
    const keys = JSON.parse(event.dataTransfer.getData(DRAG_TYPE));
    return Array.isArray(keys) ? keys : [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

const hasSelectModifier = (event) =>
  event.shiftKey || event.metaKey || event.ctrlKey;

// "N blocks" under the pointer while several blocks are dragged.
const setMultiDragImage = (event, label) => {
  const ghost = document.createElement('div');
  ghost.className = 'ns-drag-ghost';
  ghost.textContent = label;
  document.body.appendChild(ghost);
  event.dataTransfer.setDragImage(ghost, 12, 12);
  setTimeout(() => ghost.remove(), 0);
};

const allowDrop = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

// Pressing a selected row keeps the selection (it may start a drag of all of
// it); a plain click on it then selects just that row.
const BlockRow = ({
  block,
  isSelected,
  readOnly,
  onSelect,
  onRename,
  dragKeysFor,
  dragLabelFor,
}) => (
  <div
    className={'ns-block-row' + (isSelected ? ' ns-row-selected' : '')}
    draggable={!readOnly}
    onDragStart={(event) => {
      const keys = dragKeysFor(block.key);
      event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(keys));
      if (keys.length > 1) {
        setMultiDragImage(event, dragLabelFor(keys.length));
      }
    }}
    onMouseDown={(event) => {
      if (!isSelected || hasSelectModifier(event)) {
        onSelect(block.key, event);
      }
    }}
    onClick={(event) => {
      if (isSelected && !hasSelectModifier(event)) {
        onSelect(block.key, event);
      }
    }}
  >
    <span className='ns-drag-handle' aria-hidden='true'>
      ⋮⋮
    </span>
    <input
      className='ns-row-input'
      value={block.name}
      readOnly={readOnly}
      onChange={(event) => onRename(block.key, event.target.value)}
    />
  </div>
);

const FieldGroup = ({ field, isSelected, readOnly, rowProps, handlers }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  return (
    <div
      className={'ns-field-group' + (isSelected ? ' ns-row-selected' : '')}
      onDragOver={readOnly ? undefined : allowDrop}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handlers.onMoveBlocks(readDraggedKeys(event), field.key);
      }}
    >
      <div
        className='ns-field-header'
        onMouseDown={() => handlers.onSelectField(field.key)}
      >
        <button
          type='button'
          className='ns-chevron'
          onClick={() => setIsCollapsed((collapsed) => !collapsed)}
        >
          {isCollapsed ? '›' : '⌄'}
        </button>
        <input
          className='ns-row-input'
          value={field.name}
          readOnly={readOnly}
          onChange={(event) =>
            handlers.onRenameField(field.key, event.target.value)
          }
        />
      </div>
      {!isCollapsed && (
        <div className='ns-field-blocks'>
          {field.blocks.map((block) => (
            <BlockRow key={block.key} block={block} {...rowProps(block)} />
          ))}
        </div>
      )}
    </div>
  );
};

const BlockList = ({
  draft,
  selectedKeys,
  selectedFieldKey,
  readOnly,
  handlers,
}) => {
  const { t } = useTranslation();
  const selected = new Set(selectedKeys);
  // Dragging a selected row moves the whole selection; any other row alone.
  const dragKeysFor = (key) =>
    selected.has(key) ? selectedKeys.slice() : [key];
  const dragLabelFor = (count) => t('new_season_dragging_blocks', { count });
  const rowProps = (block) => ({
    isSelected: selected.has(block.key),
    readOnly,
    onSelect: handlers.onSelectBlock,
    onRename: handlers.onRenameBlock,
    dragKeysFor,
    dragLabelFor,
  });

  if (draft.fields.length === 0) {
    return <div className='ns-empty-text'>{t('new_season_empty_blocks')}</div>;
  }
  return (
    <div
      className='ns-block-list'
      onDragOver={readOnly ? undefined : allowDrop}
      onDrop={(event) => {
        event.preventDefault();
        handlers.onMoveBlocks(readDraggedKeys(event), null);
      }}
    >
      {draft.fields.map((field) =>
        isGroupField(field) ? (
          <FieldGroup
            key={field.key}
            field={field}
            isSelected={field.key === selectedFieldKey}
            readOnly={readOnly}
            rowProps={rowProps}
            handlers={handlers}
          />
        ) : (
          <BlockRow
            key={field.key}
            block={field.blocks[0]}
            {...rowProps(field.blocks[0])}
          />
        ),
      )}
    </div>
  );
};

module.exports = BlockList;
